import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiError } from '../../api/client';
import { listTemplates, quickBrew, type DashboardTemplate } from '../../api/dashboards';
import { Alert, Badge, Button, Card } from '../ui';
import './QuickBrewCard.css';

/**
 * Quick Brew: pick a template, get a dashboard.
 *
 * A template that does not fit this dataset is shown anyway, disabled, with the
 * reason in the reader's own terms — "this needs a date column, and this
 * dataset has none" tells someone what to do next; hiding the card tells them
 * nothing.
 */
export function QuickBrewCard({ versionId }: { versionId: string }) {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState<DashboardTemplate[]>([]);
  const [brewing, setBrewing] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const load = useCallback(async () => {
    try {
      setTemplates(await listTemplates(versionId));
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    }
  }, [versionId]);

  useEffect(() => {
    void load();
  }, [load]);

  const brew = async (template: DashboardTemplate) => {
    setBrewing(template.key);
    setError(null);
    try {
      const dashboard = await quickBrew({ datasetVersionId: versionId, template: template.key });
      navigate(`/dashboards/${dashboard.id}`);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : null);
    } finally {
      setBrewing(null);
    }
  };

  if (!templates.length) return null;

  return (
    <Card
      title="Quick Brew"
      description="A ready-made dashboard for this data. You can change everything afterwards."
    >
      {error && (
        <Alert tone="warning" title="That dashboard could not be brewed">
          {error.message}
        </Alert>
      )}

      <ul className="sc-brew">
        {templates.map((template) => (
          <li key={template.key} className="sc-brew__item">
            <div className="sc-brew__head">
              <h3 className="sc-brew__name">{template.name}</h3>
              {template.fits === false && <Badge tone="neutral">Does not fit</Badge>}
            </div>
            <p className="sc-brew__description">{template.description}</p>
            <p className="sc-brew__needs">
              {template.fits === false ? template.reason : `Needs: ${template.needs.join(' · ')}`}
            </p>
            <Button
              size="sm"
              variant={template.fits === false ? 'secondary' : 'primary'}
              disabled={template.fits === false || brewing !== null}
              isLoading={brewing === template.key}
              onClick={() => void brew(template)}
            >
              Brew
            </Button>
          </li>
        ))}
      </ul>
    </Card>
  );
}
